
import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { handleConnectionError, isOnline } from '@/utils/connectionErrorHandler';
import type { Json } from '@/integrations/supabase/types';
import type { SettingsTabProps } from './types';

const SEOSettingsTab: React.FC<SettingsTabProps> = ({ settings, onSettingsChange }) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isLocalPending, setIsLocalPending] = useState(false);
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const updateSettingsMutation = useMutation({
    mutationFn: async (settingsToUpdate: Record<string, any>) => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      // Check connection before attempting
      if (!isOnline()) {
        throw new Error('No internet connection. Please check your connection and try again.');
      }
      
      const updatePromises = Object.entries(settingsToUpdate).map(async ([key, value]) => {
        const jsonValue: Json = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null
          ? value
          : JSON.stringify(value);
          
        const { error } = await supabase
          .from('website_settings')
          .upsert({ 
            key, 
            value: jsonValue, 
            description: `Setting for ${key}` 
          }, {
            onConflict: 'key'
          });
          
        if (error) {
          throw error;
        }
      });
      
      // Race the promises with a timeout
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timeout')), 15000);
      });
      
      await Promise.race([Promise.all(updatePromises), timeoutPromise]);
    },
    onMutate: () => {
      setErrors({});
      setIsLocalPending(true);
    },
    onSuccess: () => {
      setLastSaved(new Date());
      setIsLocalPending(false);
      queryClient.invalidateQueries({ queryKey: ['website-settings'] });
      queryClient.invalidateQueries({ queryKey: ['seo'] });
      toast.success('SEO settings updated successfully', {
        description: 'Changes have been saved and are now live',
        duration: 4000,
      });
    },
    onError: (error: any) => {
      console.error('SEO settings save error:', error);
      setIsLocalPending(false);
      handleConnectionError(error, 'SEO Settings Update');
      
      const errorMessage = error.message || 'Failed to save settings';
      if (errorMessage.includes('connection') || errorMessage.includes('network') || errorMessage.includes('timeout')) {
        toast.error('Connection Error', {
          description: 'Please check your internet connection and try again',
          duration: 6000,
        });
      } else {
        toast.error('Failed to update SEO settings', {
          description: errorMessage,
          duration: 6000,
        });
      }
      setErrors({ seo: errorMessage });
    },
    onSettled: () => {
      // Always reset local pending state regardless of success/error
      setIsLocalPending(false);
      abortControllerRef.current = null;
    },
  });

  const validateSettings = () => {
    const newErrors: Record<string, string> = {};
    
    if (settings.meta_title && settings.meta_title.length > 60) {
      newErrors.meta_title = 'Meta title should be 60 characters or less for optimal SEO';
    }
    
    if (settings.meta_description && settings.meta_description.length > 160) {
      newErrors.meta_description = 'Meta description should be 160 characters or less for optimal SEO';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    // Prevent multiple submissions using local state
    if (isLocalPending || updateSettingsMutation.isPending) {
      console.log('Save already in progress, ignoring click');
      return;
    }

    if (!validateSettings()) {
      toast.error('Please fix the validation errors before saving');
      return;
    }

    const seoSettings = {
      meta_title: settings.meta_title,
      meta_description: settings.meta_description,
      meta_keywords: settings.meta_keywords,
      google_analytics_id: settings.google_analytics_id,
    };

    updateSettingsMutation.mutate(seoSettings);
  };

  const isPending = isLocalPending || updateSettingsMutation.isPending;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          SEO Settings
          {lastSaved && (
            <span className="flex items-center gap-1 text-sm text-green-600 font-normal">
              <CheckCircle className="w-4 h-4" />
              Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </CardTitle>
        <CardDescription>Configure search engine optimization settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {errors.seo && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            {errors.seo}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="meta_title">Meta Title</Label>
          <Input
            id="meta_title"
            value={settings.meta_title || ''}
            onChange={(e) => onSettingsChange('meta_title', e.target.value)}
            placeholder="Your website title for search engines"
            className={errors.meta_title ? 'border-red-500' : ''}
            disabled={isPending}
          />
          <div className="flex justify-between text-sm">
            {errors.meta_title && (
              <p className="text-red-600">{errors.meta_title}</p>
            )}
            <p className="text-gray-500 ml-auto">
              {(settings.meta_title || '').length}/60 characters
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="meta_description">Meta Description</Label>
          <Textarea
            id="meta_description"
            value={settings.meta_description || ''}
            onChange={(e) => onSettingsChange('meta_description', e.target.value)}
            placeholder="A brief description of your website for search engines"
            rows={3}
            className={errors.meta_description ? 'border-red-500' : ''}
            disabled={isPending}
          />
          <div className="flex justify-between text-sm">
            {errors.meta_description && (
              <p className="text-red-600">{errors.meta_description}</p>
            )}
            <p className="text-gray-500 ml-auto">
              {(settings.meta_description || '').length}/160 characters
            </p>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="meta_keywords">Meta Keywords</Label>
          <Textarea
            id="meta_keywords"
            value={settings.meta_keywords || ''}
            onChange={(e) => onSettingsChange('meta_keywords', e.target.value)}
            placeholder="Comma-separated keywords relevant to your website"
            rows={2}
            disabled={isPending}
          />
          <p className="text-sm text-gray-500">
            Separate keywords with commas. Note: Most search engines no longer use meta keywords for ranking.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="google_analytics_id">Google Analytics ID</Label>
          <Input
            id="google_analytics_id"
            value={settings.google_analytics_id || ''}
            onChange={(e) => onSettingsChange('google_analytics_id', e.target.value)}
            placeholder="G-XXXXXXXXXX or UA-XXXXXXXXX-X"
            disabled={isPending}
          />
          <p className="text-sm text-gray-500">
            Enter your Google Analytics tracking ID to enable website analytics
          </p>
        </div>

        <div className="flex justify-end pt-4">
          <Button 
            onClick={handleSave} 
            disabled={isPending || !isOnline()}
            className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            {isPending ? 'Saving...' : 'Save SEO Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SEOSettingsTab;
